import { ConnectorType, useConnectorsStore } from "@/stores/connectorsStore";
import { useEffect, useMemo } from "react";
import { ActionTriggerDefinition, useActions, useTriggers } from "./useActionTriggerDefinitions";

const B3OS_ICON_CDN_BASE = "https://cdn.b3.fun/b3os-icons";

export interface App {
  slug: string;
  name: string;
  description: string;
  logoUrl?: string;
  categories?: string[]; // Deprecated: use tags
  tags?: string[];
  triggerCount: number;
  actionCount: number;
  totalIntegrations: number;
  isSpecialCategory?: boolean; // For special categories like "Triggers" and "x402"
}

// Special category slugs
export const SPECIAL_CATEGORY_TRIGGERS = "_triggers";
export const SPECIAL_CATEGORY_ACTIONS = "_actions";
export const SPECIAL_CATEGORY_X402 = "_x402";

// Known app slugs
export const APP_SLUG_TURNKEY = "wallet";

// Format a slug into a display name (e.g., "slack" -> "Slack", "google-sheets" -> "Google Sheets")
function formatName(slug: string): string {
  return slug
    .split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Normalize connector type to slug.
// Native connectors: "discord" -> "discord"
// Pipedream connectors: "pipedream:discord" -> "pipedream-discord"
// This prevents URL collisions when a Pipedream connector shares a name with a native one.
function normalizeToSlug(type: string): string {
  return type.replace(":", "-").toLowerCase();
}

// Get the app slug from an integration item (trigger or action)
// Uses platform field if available, otherwise falls back to connector.type
function getAppSlug(item: ActionTriggerDefinition): string | null {
  if (item.platform) {
    return item.platform.toLowerCase();
  }
  if (item.connector?.type) {
    return normalizeToSlug(item.connector.type);
  }
  return null;
}

// Check if an action is an x402 endpoint (type starts with "x402ep_" prefix)
function isX402Action(action: ActionTriggerDefinition): boolean {
  return action.type.startsWith("x402ep_");
}

// Build app list from triggers, actions, and connector types
function buildAppList(
  triggers: ActionTriggerDefinition[],
  actions: ActionTriggerDefinition[],
  connectorTypes: ConnectorType[],
): App[] {
  // Extract unique app slugs from triggers and actions
  // Use platform field if available, otherwise use connector.type
  const slugSet = new Set<string>();
  [...triggers, ...actions].forEach(item => {
    const slug = getAppSlug(item);
    if (slug) {
      slugSet.add(slug);
    }
  });

  // Create connector type lookup by normalized slug
  // Prefer non-pipedream connectors (they usually have better metadata)
  const connectorBySlug = new Map<string, ConnectorType>();
  connectorTypes.forEach(ct => {
    const slug = normalizeToSlug(ct.type);
    const existing = connectorBySlug.get(slug);
    // Prefer non-pipedream connectors, or if no existing one
    if (!existing || (existing.provider === "pipedream" && !ct.provider)) {
      connectorBySlug.set(slug, ct);
    }
  });

  // Build app list
  const apps: App[] = Array.from(slugSet).map(slug => {
    const connector = connectorBySlug.get(slug);
    const appTriggers = triggers.filter(t => getAppSlug(t) === slug);
    const appActions = actions.filter(a => getAppSlug(a) === slug);

    return {
      slug,
      name: connector?.name || formatName(slug),
      description: connector?.description || `Integrations for ${formatName(slug)}`,
      logoUrl: connector?.logoUrl,
      categories: connector?.categories,
      tags: connector?.tags || connector?.categories?.map(c => c.toLowerCase().replace(/ /g, "-")),
      triggerCount: appTriggers.length,
      actionCount: appActions.length,
      totalIntegrations: appTriggers.length + appActions.length,
    };
  });

  // Add special categories at the beginning
  const specialApps: App[] = [];

  // "Triggers" special category - all ways to start workflows
  if (triggers.length > 0) {
    specialApps.push({
      slug: SPECIAL_CATEGORY_TRIGGERS,
      name: "Triggers",
      description: "All ways to start your workflows",
      logoUrl: `${B3OS_ICON_CDN_BASE}/triggers.svg`,
      triggerCount: triggers.length,
      actionCount: 0,
      totalIntegrations: triggers.length,
      isSpecialCategory: true,
    });
  }

  // "Actions" special category - all actions
  if (actions.length > 0) {
    specialApps.push({
      slug: SPECIAL_CATEGORY_ACTIONS,
      name: "Actions",
      description: "All actions you can use in your workflows",
      logoUrl: `${B3OS_ICON_CDN_BASE}/actions.svg`,
      triggerCount: 0,
      actionCount: actions.length,
      totalIntegrations: actions.length,
      isSpecialCategory: true,
    });
  }

  // "x402" special category - payment-gated API endpoints
  const x402Actions = actions.filter(isX402Action);
  if (x402Actions.length > 0) {
    specialApps.push({
      slug: SPECIAL_CATEGORY_X402,
      name: "x402 Endpoints",
      description: "Open ecosystem endpoints",
      logoUrl: `${B3OS_ICON_CDN_BASE}/x402.svg`,
      triggerCount: 0,
      actionCount: x402Actions.length,
      totalIntegrations: x402Actions.length,
      isSpecialCategory: true,
    });
  }

  return [...specialApps, ...apps];
}

export function useApps() {
  const { triggers, isLoading: triggersLoading } = useTriggers();
  const { actions, isLoading: actionsLoading } = useActions();
  const { connectorTypes, loadConnectorTypes } = useConnectorsStore();

  // Load connector types on mount
  useEffect(() => {
    if (connectorTypes.length === 0) {
      loadConnectorTypes();
    }
  }, [connectorTypes.length, loadConnectorTypes]);

  const apps = useMemo(() => {
    return buildAppList(triggers, actions, connectorTypes);
  }, [triggers, actions, connectorTypes]);

  // Combine all integrations for search
  const allIntegrations = useMemo(() => {
    const triggersWithType = triggers.map((t: ActionTriggerDefinition) => ({
      ...t,
      integrationType: "trigger" as const,
    }));
    const actionsWithType = actions.map((a: ActionTriggerDefinition) => ({
      ...a,
      integrationType: "action" as const,
    }));
    return [...triggersWithType, ...actionsWithType];
  }, [triggers, actions]);

  return {
    apps,
    triggers,
    actions,
    allIntegrations,
    isLoading: triggersLoading || actionsLoading,
  };
}

// Hook to get a single app with its triggers and actions
export function useAppBySlug(slug: string) {
  const { triggers, isLoading: triggersLoading } = useTriggers();
  const { actions, isLoading: actionsLoading } = useActions();
  const { connectorTypes, loadConnectorTypes } = useConnectorsStore();

  useEffect(() => {
    if (connectorTypes.length === 0) {
      loadConnectorTypes();
    }
  }, [connectorTypes.length, loadConnectorTypes]);

  const result = useMemo(() => {
    const normalizedSlug = slug.toLowerCase();

    // Handle special category: Triggers
    if (normalizedSlug === SPECIAL_CATEGORY_TRIGGERS) {
      return {
        app: {
          slug: SPECIAL_CATEGORY_TRIGGERS,
          name: "Triggers",
          description: "All ways to start your workflows",
          logoUrl: `${B3OS_ICON_CDN_BASE}/triggers.svg`,
          triggerCount: triggers.length,
          actionCount: 0,
          totalIntegrations: triggers.length,
          isSpecialCategory: true,
        },
        triggers: triggers,
        actions: [],
        connectorType: undefined,
      };
    }

    // Handle special category: Actions
    if (normalizedSlug === SPECIAL_CATEGORY_ACTIONS) {
      return {
        app: {
          slug: SPECIAL_CATEGORY_ACTIONS,
          name: "Actions",
          description: "All actions you can use in your workflows",
          logoUrl: `${B3OS_ICON_CDN_BASE}/actions.svg`,
          triggerCount: 0,
          actionCount: actions.length,
          totalIntegrations: actions.length,
          isSpecialCategory: true,
        },
        triggers: [],
        actions: actions,
        connectorType: undefined,
      };
    }

    // Handle special category: x402 Endpoints
    if (normalizedSlug === SPECIAL_CATEGORY_X402) {
      const x402Actions = actions.filter((a: ActionTriggerDefinition) => isX402Action(a));
      return {
        app: {
          slug: SPECIAL_CATEGORY_X402,
          name: "x402 Endpoints",
          description: "Payment-gated API endpoints",
          logoUrl: `${B3OS_ICON_CDN_BASE}/x402.svg`,
          triggerCount: 0,
          actionCount: x402Actions.length,
          totalIntegrations: x402Actions.length,
          isSpecialCategory: true,
        },
        triggers: [],
        actions: x402Actions,
        connectorType: undefined,
      };
    }

    // Find matching triggers and actions (by platform or connector.type)
    const appTriggers = triggers.filter((t: ActionTriggerDefinition) => getAppSlug(t) === normalizedSlug);
    const appActions = actions.filter((a: ActionTriggerDefinition) => getAppSlug(a) === normalizedSlug);

    // Find connector type for metadata (prefer non-pipedream)
    let connector: ConnectorType | undefined;
    for (const ct of connectorTypes) {
      const ctSlug = normalizeToSlug(ct.type);
      if (ctSlug === normalizedSlug) {
        if (!connector || (connector.provider === "pipedream" && !ct.provider)) {
          connector = ct;
        }
      }
    }

    if (appTriggers.length === 0 && appActions.length === 0) {
      return {
        app: null,
        triggers: [],
        actions: [],
        connectorType: undefined,
      };
    }

    return {
      app: {
        slug: normalizedSlug,
        name: connector?.name || formatName(normalizedSlug),
        description: connector?.description || `Integrations for ${formatName(normalizedSlug)}`,
        logoUrl: connector?.logoUrl,
        categories: connector?.categories,
        tags: connector?.tags || connector?.categories?.map(c => c.toLowerCase().replace(/ /g, "-")),
        triggerCount: appTriggers.length,
        actionCount: appActions.length,
        totalIntegrations: appTriggers.length + appActions.length,
      },
      triggers: appTriggers,
      actions: appActions,
      connectorType: connector,
    };
  }, [slug, triggers, actions, connectorTypes]);

  return {
    ...result,
    isLoading: triggersLoading || actionsLoading,
  };
}
